import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

/**
 * Lightweight onboarding tour. Shows a stack of tooltip-style cards, one at a
 * time, positioned relative to the target element (identified by data-testid).
 * Persists dismissal in localStorage so it never shows twice for the same user.
 */
const STEPS = [
  {
    tid: "nav-dashboard",
    title: "Welcome to Treasurescare 👋",
    body: "Your dashboard is home base — see all your covered devices, active claims and plan at a glance.",
  },
  {
    tid: "nav-devices",
    title: "Register your first device",
    body: "Add your phone here — brand, model, IMEI, purchase proof. Photos help our AI assess damage later.",
  },
  {
    tid: "nav-subscription",
    title: "Pick a plan",
    body: "Finder (free), Seeker, Hunter, Vault or Household — choose the level of cover. You can switch anytime.",
  },
  {
    tid: "nav-claims",
    title: "File a claim in 4 steps",
    body: "Damage? Loss? Snap a photo, describe what happened, and our AI will give you an instant assessment.",
  },
  {
    tid: "chat-widget-open",
    title: "Meet Aegis, your AI assistant",
    body: "Stuck? Ask Aegis anything about your cover, claims or repairs — 24/7.",
  },
];

const STORAGE_KEY = "tc-onboarding-done";

export default function OnboardingTour({ userId }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const done = localStorage.getItem(`${STORAGE_KEY}:${userId}`);
    if (done) return;
    // Small delay to let the layout paint
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [userId]);

  useEffect(() => {
    if (!visible) return;
    const s = STEPS[step];
    if (!s) return;
    const el = document.querySelector(`[data-testid="${s.tid}"]`);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
      height: r.height,
    });
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [step, visible]);

  const dismiss = () => {
    if (userId) localStorage.setItem(`${STORAGE_KEY}:${userId}`, "1");
    setVisible(false);
  };
  const next = () => {
    if (step >= STEPS.length - 1) dismiss();
    else setStep(step + 1);
  };
  const back = () => setStep(Math.max(0, step - 1));

  if (!visible) return null;

  const current = STEPS[step];
  // Position tooltip: if we have a rect, place it to the right of it, else centre
  const tooltipStyle = rect
    ? {
        position: "absolute",
        top: rect.top,
        left: rect.left + rect.width + 12,
        maxWidth: 340,
      }
    : {
        position: "fixed",
        top: "40%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: 340,
      };

  return (
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid="onboarding-tour">
      {/* Dim backdrop */}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />

      {/* Highlight cutout */}
      {rect && (
        <div
          className="absolute border-2 border-primary rounded-lg pointer-events-none animate-pulse"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        style={tooltipStyle}
        className="pointer-events-auto rounded-xl bg-card border border-border shadow-2xl p-5 z-10"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-secondary">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Getting started · {step + 1}/{STEPS.length}</span>
          </div>
          <button onClick={dismiss} data-testid="tour-close-btn">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <h3 className="mt-2 font-display text-lg font-semibold">{current.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{current.body}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={dismiss} data-testid="tour-skip-btn">
            Skip tour
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={back} data-testid="tour-back-btn">
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button size="sm" onClick={next} data-testid="tour-next-btn">
              {step >= STEPS.length - 1 ? "Finish" : "Next"}
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
